import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { Row, Col, Button } from 'reactstrap';
import EditSharpIcon from '@mui/icons-material/EditSharp';
import Sidebar from '../../Sidebar';
import ErrorToast from '../../ErrorToast';
import LoadingOverlay from '../../components/LoadingOverlay';
import FloatingControls from '../../components/FloatingControls';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import ReactTablePopUpSubmitWrapper from '../../components/ReactTablePopUpSubmitWrapper';
import { displayError } from '../../helpers';
import {
    getPurchaseInvoiceDetails,
    updatePurchaseInvoice,
} from '../../services/PurchaseInvoiceServices';
import PiUploadCentreModal from './PiUploadCentreModal';
import { mapSavedGrnDocument } from '../store/grnDocumentUtils';
import { formatAmount, formatQty } from '../../utils/listSummaryUtils';

function PurchaseInvoiceEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [inProgress, setInProgress] = useState(false);
    const [invoice, setInvoice] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [showUploadCentre, setShowUploadCentre] = useState(false);
    const [showGrnDetailsModal, setShowGrnDetailsModal] = useState(false);
    const [grnDetailsRows, setGrnDetailsRows] = useState([]);
    const [grnDetailsTitle, setGrnDetailsTitle] = useState('List of GRN');
    const [inputs, setInputs] = useState({
        remarks: '',
        freight_charges: 0,
        discount_amt: 0,
        tcs_amt: 0,
    });

    const grnDetailsColumns = useMemo(() => ([
        { fieldName: 'grn_no', headerName: 'GRN No', size: 160 },
        { fieldName: 'grn_date', headerName: 'GRN Date', size: 100 },
        { fieldName: 'dc_no', headerName: 'DC No', size: 100 },
        { fieldName: 'vehicle_no', headerName: 'Vehicle', size: 110 },
        { fieldName: 'unit', headerName: 'Unit', size: 70 },
        { fieldName: 'dc_qty_display', headerName: 'DC Qty', size: 90, align: 'right' },
        { fieldName: 'accepted_qty_display', headerName: 'Acc Qty', size: 90, align: 'right' },
        { fieldName: 'basic_rate_display', headerName: 'Rate', size: 90, align: 'right' },
        { fieldName: 'taxable_amt_display', headerName: 'Taxable', size: 110, align: 'right' },
    ]), []);

    const totals = useMemo(() => {
        const taxable = Number(invoice?.taxable_amt) || 0;
        const gst = Number(invoice?.gst_amt) || 0;
        const cgst = Number(invoice?.cgst_amt) || 0;
        const sgst = Number(invoice?.sgst_amt) || 0;
        const igst = Number(invoice?.igst_amt) || 0;
        const gross = taxable + gst;
        const freight = Number(inputs.freight_charges) || 0;
        const discount = Number(inputs.discount_amt) || 0;
        const tcs = Number(inputs.tcs_amt) || 0;
        const baseAmount = Number((taxable + gst + freight + tcs - discount).toFixed(2));
        const invoiceAmt = Math.round(baseAmount);
        const roundedOff = Number((invoiceAmt - baseAmount).toFixed(2));
        return {
            taxable: taxable.toFixed(2),
            gst: gst.toFixed(2),
            cgst: cgst.toFixed(2),
            sgst: sgst.toFixed(2),
            igst: igst.toFixed(2),
            gross: gross.toFixed(2),
            rounded_off: roundedOff.toFixed(2),
            invoice_amt: invoiceAmt.toFixed(2),
        };
    }, [invoice, inputs.freight_charges, inputs.discount_amt, inputs.tcs_amt]);

    const showGrnDetails = (row) => {
        const details = row.grn_details || [];
        if (!details.length) {
            return;
        }
        setGrnDetailsTitle(`List of GRN — ${row.product_name || 'Product'}`);
        setGrnDetailsRows(details.map((item) => ({
            ...item,
            dc_qty_display: formatQty(item.dc_qty),
            accepted_qty_display: formatQty(item.accepted_qty),
            basic_rate_display: formatAmount(item.basic_rate),
            taxable_amt_display: formatAmount(item.taxable_amt),
        })));
        setShowGrnDetailsModal(true);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((current) => ({ ...current, [name]: value }));
    };

    useEffect(() => {
        if (!id) {
            return;
        }
        setInProgress(true);
        getPurchaseInvoiceDetails(cookies, id)
            .then((response) => {
                setInvoice(response);
                setDocuments((response.documents || []).map(mapSavedGrnDocument));
                setInputs({
                    remarks: response.user_remarks || '',
                    freight_charges: Number(response.freight_charges) || 0,
                    discount_amt: Number(response.discount_amt) || 0,
                    tcs_amt: Number(response.tcs_amt) || 0,
                });
                setInProgress(false);
            })
            .catch((error) => {
                setInProgress(false);
                displayError(error.response?.data ?? error.message, 'Failed to load invoice');
            });
    }, [cookies, id]);

    const handleSubmit = (event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Do you want to Update?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            updatePurchaseInvoice(cookies, id, {
                user_remarks: inputs.remarks || '',
                freight_charges: Number(inputs.freight_charges) || 0,
                discount_amt: Number(inputs.discount_amt) || 0,
                tcs_amt: Number(inputs.tcs_amt) || 0,
                rounded_off: Number(totals.rounded_off),
                invoice_amt: Number(totals.invoice_amt),
            })
                .then(() => {
                    setInProgress(false);
                    Swal.fire('Updated!', '', 'success').then(() => navigate('/PurchaseInvoiceTable'));
                })
                .catch((error) => {
                    setInProgress(false);
                    displayError(error.response?.data ?? error.message, 'Update Failed');
                });
        });
    };

    return (
        <>
            <div id="outer-container" className="App">
                <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
                <ErrorToast />
                <LoadingOverlay inProgress={inProgress} />
                <div id="page-wrap">
                    <div className="premium-page-header-wrap">
                        <PremiumPageHeader
                            module="PURCHASE"
                            title="Update Purchase Invoice"
                            subtitle="Edit invoice charges, remarks, and upload supporting documents."
                            modeLabel="EDIT MODE"
                        />
                    </div>
                    <div className="list-form-page-shell">
                        <form onSubmit={handleSubmit}>
                            <FloatingControls
                                tableLink="/PurchaseInvoiceTable"
                                onCancel={() => navigate('/PurchaseInvoiceTable')}
                                enableCancel={true}
                            />
                            {invoice && (
                                <div className="container form-control-panel">
                                    <Row>
                                        <Col xl={6} lg={12} md={12}>
                                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                                <label className="form-group col-sm-4 text-right">Invoice Mode</label>
                                                <input
                                                    className="form-control col-sm-7"
                                                    value={invoice.invoice_mode === 'without_grn' ? 'Without GRN' : 'With GRN'}
                                                    disabled
                                                    style={{ cursor: 'not-allowed' }}
                                                />

                                                <label className="form-group col-sm-4 text-right">Plant Location</label>
                                                <input
                                                    className="form-control col-sm-7"
                                                    value={invoice.plant?.alias || invoice.plant?.name || ''}
                                                    disabled
                                                    style={{ cursor: 'not-allowed' }}
                                                />

                                                <label className="form-group col-sm-4 text-right">Vendor</label>
                                                <input
                                                    className="form-control col-sm-7"
                                                    value={invoice.vendor?.name || ''}
                                                    disabled
                                                    style={{ cursor: 'not-allowed' }}
                                                />

                                                {invoice.invoice_mode !== 'without_grn' && (
                                                    <div className="w-100 d-flex align-items-center justify-content-between px-2 mt-2">
                                                        <span className="d-inline-flex align-items-center">
                                                            <label className="mb-0 mr-1">Linked GRN</label>
                                                        </span>
                                                        <span className="text-muted">
                                                            Selected: {(invoice.grn_list || []).length}
                                                        </span>
                                                    </div>
                                                )}

                                                <div className="w-100 d-flex align-items-center px-2 mt-2">
                                                    <span className="d-inline-flex align-items-center">
                                                        <label className="mb-0 mr-1">Upload Centre</label>
                                                        <EditSharpIcon
                                                            onClick={() => setShowUploadCentre(true)}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                color: documents.length ? '#1565c0' : '#d32f2f',
                                                            }}
                                                        />
                                                    </span>
                                                    <span className="ml-2 text-muted">
                                                        {documents.length} file(s)
                                                    </span>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col xl={6} lg={12} md={12}>
                                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                                <label className="form-group col-sm-4 text-right">Invoice No</label>
                                                <input
                                                    className="form-control col-sm-7"
                                                    value={`${invoice.prefix || ''}${invoice.invoice_no || ''}`}
                                                    disabled
                                                    style={{ cursor: 'not-allowed' }}
                                                />
                                                <label className="form-group col-sm-4 text-right">Invoice Date</label>
                                                <input
                                                    className="form-control col-sm-7"
                                                    value={invoice.invoice_date || ''}
                                                    disabled
                                                    style={{ cursor: 'not-allowed' }}
                                                />
                                                <label className="form-group col-sm-4 text-right">Remarks</label>
                                                <input
                                                    name="remarks"
                                                    className="form-control col-sm-7"
                                                    value={inputs.remarks || ''}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-group col-sm-4 text-right">Invoice Amount</label>
                                                <input
                                                    className="form-control col-sm-7"
                                                    value={formatAmount(totals.invoice_amt)}
                                                    disabled
                                                    style={{ cursor: 'not-allowed', fontWeight: 700 }}
                                                />
                                            </div>
                                        </Col>
                                    </Row>

                                    {(invoice.detail_list || []).length > 0 && (
                                        <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                                            <div className="w-100 px-1 pb-2">
                                                <h5 className="mb-0"><b>INVOICE LINES</b></h5>
                                            </div>
                                            <table
                                                className="table-bordered table-responsive searchTable table order-list"
                                                id="piLinesTableView"
                                                style={{
                                                    textAlign: 'center',
                                                    borderRadius: '10px',
                                                    backgroundColor: 'RGB(188,232,253)',
                                                    border: '4px solid rgb(235, 73, 73)',
                                                }}
                                            >
                                                <thead>
                                                    <tr style={{ backgroundColor: 'RGB(188,232,253)' }}>
                                                        <th>Product Code</th>
                                                        <th>Unit</th>
                                                        <th>DC Qty</th>
                                                        <th>Acc/Invoice Qty</th>
                                                        <th>Basic Rate</th>
                                                        <th>Taxable Amount</th>
                                                        <th>GST %</th>
                                                        <th>CGST Amount</th>
                                                        <th>SGST Amount</th>
                                                        <th>IGST Amount</th>
                                                        <th>GST Amount</th>
                                                        <th>Gross Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(invoice.detail_list || []).map((row) => (
                                                        <tr key={row.id}>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    style={{ width: '100px', cursor: 'pointer' }}
                                                                    value={row.product_name || ''}
                                                                    readOnly
                                                                    title="Click to view GRN list"
                                                                    onClick={() => showGrnDetails(row)}
                                                                />
                                                            </td>
                                                            <td>{row.unit || ''}</td>
                                                            <td>{formatQty(row.dc_qty || row.invoice_qty || 0)}</td>
                                                            <td>{formatQty(row.invoice_qty)}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatAmount(row.basic_rate)}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatAmount(row.taxable_amt)}</td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>{row.tax_label || `GST @ ${row.tax_perc || 0}%`}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatAmount(row.cgst_amt)}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatAmount(row.sgst_amt)}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatAmount(row.igst_amt)}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatAmount(row.gst_amt)}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatAmount(row.gross_amt || (Number(row.taxable_amt || 0) + Number(row.gst_amt || 0)))}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                                        <table
                                            className="table order-list"
                                            id="piTotalsTableView"
                                            style={{
                                                textAlign: 'center',
                                                borderRadius: '10px',
                                                backgroundColor: 'white',
                                                border: '4px solid rgb(235, 73, 73)',
                                            }}
                                        >
                                            <tbody>
                                                <tr>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>Total Taxable Amount </b></td>
                                                    <td colSpan="4">
                                                        <input type="text" className="form-control" style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }} value={formatAmount(totals.taxable)} readOnly />
                                                    </td>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>CGST Amount </b></td>
                                                    <td colSpan="4">
                                                        <input type="text" className="form-control" style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }} value={formatAmount(totals.cgst)} readOnly />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>Total GST Amount </b></td>
                                                    <td colSpan="4">
                                                        <input type="text" className="form-control" style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }} value={formatAmount(totals.gst)} readOnly />
                                                    </td>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>SGST Amount </b></td>
                                                    <td colSpan="4">
                                                        <input type="text" className="form-control" style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }} value={formatAmount(totals.sgst)} readOnly />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>Total Gross Amount </b></td>
                                                    <td colSpan="4">
                                                        <input type="text" className="form-control" style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }} value={formatAmount(totals.gross)} readOnly />
                                                    </td>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>IGST Amount </b></td>
                                                    <td colSpan="4">
                                                        <input type="text" className="form-control" style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }} value={formatAmount(totals.igst)} readOnly />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>Freight </b></td>
                                                    <td colSpan="4">
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            name="freight_charges"
                                                            className="form-control"
                                                            style={{ backgroundColor: 'white', textAlign: 'right' }}
                                                            value={inputs.freight_charges}
                                                            onChange={handleChange}
                                                        />
                                                    </td>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>Discount </b></td>
                                                    <td colSpan="4">
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            name="discount_amt"
                                                            className="form-control"
                                                            style={{ backgroundColor: 'white', textAlign: 'right' }}
                                                            value={inputs.discount_amt}
                                                            onChange={handleChange}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>Round off </b></td>
                                                    <td colSpan="4">
                                                        <input type="text" className="form-control" style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right' }} value={formatAmount(totals.rounded_off)} readOnly />
                                                    </td>
                                                    <td colSpan="6" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>TCS Amount </b></td>
                                                    <td colSpan="4">
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            name="tcs_amt"
                                                            className="form-control"
                                                            style={{ backgroundColor: 'white', textAlign: 'right' }}
                                                            value={inputs.tcs_amt}
                                                            onChange={handleChange}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="14" style={{ textAlign: 'right', backgroundColor: 'white' }}><b>Total Invoice Amount </b></td>
                                                    <td colSpan="4">
                                                        <input type="text" className="form-control" style={{ backgroundColor: 'white', cursor: 'not-allowed', textAlign: 'right', fontWeight: 700 }} value={formatAmount(totals.invoice_amt)} readOnly />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="footer text-center pb-3">
                                        <Button type="submit" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }}>
                                            Update
                                        </Button>
                                        &nbsp;&nbsp;
                                        <Button
                                            type="button"
                                            className="btn btn-twitter"
                                            style={{ width: '80px', fontWeight: 'bold' }}
                                            onClick={() => navigate('/PurchaseInvoiceTable')}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>

                    <PiUploadCentreModal
                        isOpen={showUploadCentre}
                        toggle={() => setShowUploadCentre(false)}
                        cookies={cookies}
                        invoiceId={id}
                        documents={documents}
                        onDocumentsChange={setDocuments}
                    />

                    <div className="modal" tabIndex="-1" style={{ display: showGrnDetailsModal ? 'block' : 'none' }}>
                        <div className="modal-dialog modal-xl so-picker-modal-dialog">
                            <div className="modal-content">
                                <div className="modal-body">
                                    <div className="container item-list-table-container so-picker-table-wrap">
                                        <ReactTablePopUpSubmitWrapper
                                            title={grnDetailsTitle}
                                            columns={grnDetailsColumns}
                                            data={grnDetailsRows}
                                            isLoading={false}
                                            onClose={() => setShowGrnDetailsModal(false)}
                                            onRowSubmit={() => {}}
                                            initialPageSize={15}
                                            compactRows={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default PurchaseInvoiceEdit;
